import { useStore } from '@/hooks/useStore';
import { isPathEk } from '@/utils/routes';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ButtonCus } from '@/components/common/Button';
import { useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '@/utils/formatNumber';
import { flowResult } from 'mobx';
import yup from '@/services/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Checkbox } from '@/components/common/Checkbox';
import { getFromSession, removeFromSession, saveToSession } from '@/utils/browsers';
import { formatTotalTime } from '@/utils/datetime';
import { COMMON_TYPE, COUPON_TYPE, OPTION_FORMAT } from '@/types/enums';
import { ColumnDef } from '@tanstack/react-table';
import { OptionBookingTable } from '@/types/option';
import Table from '@/components/table/Table';
import CouponItem from './components/CouponItem';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Input } from '@/components/common/Input';
import { CouponBookingItem } from '@/types/coupon';
import { formatToCurrency } from '@/utils/formatNumber';
import moment from 'moment';
import { DISPPLAY_MOMENT_YMD_HM_FULLWIDTH, ekDomain, ROUTES, WORDPRESS_PATH } from '@/configs/constants';
import StepWrapper from '../components/step-container/StepWrapper';
import { cn } from '@/utils/utils';
import AnimateButton from '@/components/general/button-custom/AnimateButton';
import { XCusIcon } from '@/components/icon/XCusIcon';
import { URL_CONFIG } from '@/configs/environment';

export default observer(function StepThirdBookingPage() {
    // hook
    const { t } = useTranslation();
    const { id, bikeId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid');
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    const type = isEk ? COMMON_TYPE.EK : COMMON_TYPE.GAS;

    const handleDefaultRedirect = () => {
        window.location.href = (
            isEk ?
                `/${ekDomain}/${ROUTES.locationList.href}/detail/${id}/bike-booking-first/${bikeId}` :
                `/${ROUTES.locationList.href}/detail/${id}/bike-booking-first/${bikeId}`
        );
    };

    if (!uuid) {
        handleDefaultRedirect();
    } else {
        const data = getFromSession(uuid);
        if (!data || !data.bikeId || !data.locationId || (data.locationId && data.locationId !== id) || (data.bikeId && data.bikeId !== bikeId)) {
            handleDefaultRedirect();
        }
    }

    const dataSession = getFromSession(uuid || '');

    // store
    const {
        bookingStore: {
            getBikeBookingDetail, postCheckingBookingData, postPayment, getCouponOneTimeInfo, couponForever, getCouponForeverInfo
        }
    } = useStore();

    // state
    const [infor, setInfor] = useState<any>(undefined);
    const [searchCoupon, setSearchCoupon] = useState<string | undefined>('');
    const [coupon, setCoupon] = useState<string | undefined>('');
    const [couponList, setCouponList] = useState<CouponBookingItem[]>([]);

    // validate schema
    const validateSchema = yup.object().shape({
        agree: yup.boolean().default(false).optional()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            agree: false
        }
    });

    const watchAgree = form.watch('agree');

    // lifecycle
    useEffect(() => {
        if (uuid) {
            getBikeBookingDetail(Number(bikeId), Number(id), type);
            onGetCouponForever();
            // onFetch(dataSession, true);
        }
    }, [uuid]);

    // function
    const onFetch = async (payload: any, isCheckCoupon: boolean = true) => {
        const data: any = {
            bikeId: bikeId,
            locationId: id,
            startTime: payload?.startTime,
            endTime: payload?.endTime,
            additionalServices: payload?.additionalServices,
            isCheckCoupon,
            timeLocked: payload.timeLocked,
            expireTimeLocked: payload.expireTimeLocked
        };

        if (payload?.couponSelectedCode) {
            data.couponSelectedCode = payload?.couponSelectedCode;
        }

        if (payload?.couponCodeList) {
            data.couponCodeList = payload?.couponCodeList;
        }

        const res = await flowResult(postCheckingBookingData(data));
        if (res) {
            setInfor({ ...res });
            if (res.couponSelected && res.couponSelected.couponCode) {
                setCoupon(res.couponSelected.couponCode);
                // const dataSession = getFromSession(uuid || '');

                uuid && saveToSession(uuid, {
                    ...payload,
                    couponSelectedCode: res.couponSelected.couponCode
                });
            }
            if (res.couponList && res.couponList.length > 0) {
                setCouponList(res.couponList || []);
            }
        }
    };

    const onGetCouponForever = async () => {
        const res = await flowResult(getCouponForeverInfo());
        if (res) {
            let dataSession = getFromSession(uuid || '');
            let couponList = dataSession?.couponCodeList || [];
            if (!couponList.includes(res.couponCode)) {
                dataSession = {
                    ...dataSession,
                    couponCodeList: [...couponList, res.couponCode]
                };
            }
            onFetch(dataSession, true);
        }
    };

    const onPayment = async () => {
        const payload = {
            bikeId: !isNaN(Number(bikeId)) ? Number(bikeId) : null,
            locationId: !isNaN(Number(id)) ? Number(id) : null,
            startTime: dataSession?.startTime,
            endTime: dataSession?.endTime,
            additionalServices: dataSession?.additionalServices,
            couponCodeList: dataSession?.couponCodeList,
            couponSelectedCode: dataSession?.couponSelectedCode,
            timeLocked: dataSession.timeLocked,
            expireTimeLocked: dataSession.expireTimeLocked
        };
        const res = await flowResult(postPayment(payload));
        if (res) {
            window.location.href = res;
            uuid && removeFromSession([uuid]);
        }
    };

    const onBack = () => {
        navigate(
            isEk ?
                `/${ekDomain}/${ROUTES.locationList.href}/detail/${id}/bike-booking-second/${bikeId}?uuid=${uuid}` :
                `/${ROUTES.locationList.href}/detail/${id}/bike-booking-second/${bikeId}?uuid=${uuid}`
        );
    };

    const onAddCoupon = async () => {
        if (searchCoupon && searchCoupon.trim()) {
            const res = await flowResult(getCouponOneTimeInfo(searchCoupon.trim()));
            if (res) {
                const dataSession = getFromSession(uuid || '');
                let couponCodeList = dataSession?.couponCodeList && dataSession?.couponCodeList.length > 0 ? [...dataSession.couponCodeList, searchCoupon.trim()] : [searchCoupon.trim()];
                uuid && saveToSession(uuid, {
                    ...dataSession,
                    couponCodeList
                });
                onFetch({
                    ...dataSession,
                    couponCodeList
                });
            }
        }
    };

    const [totalTime, totalTimeCost] = useMemo(() => {
        let totalTime = '', totalTimeCost = 0;
        if (dataSession) {
            totalTimeCost = dataSession.dateAndTimeCost;
            if (dataSession.dateAndTime) {
                totalTime = formatTotalTime(dataSession.dateAndTime, t);
            }
        }
        return [totalTime, totalTimeCost];
    }, [dataSession]);

    const compensationFee = useMemo((): number => {
        const compensationFeeNumber = isNaN(Number(infor?.additionalServiceCost?.compensationFee)) ?
            0 :
            Number(infor?.additionalServiceCost?.compensationFee);
        const underTwentyYearsOldFeeNumber = isNaN(Number(infor?.additionalServiceCost?.underTwentyYearsOldFee)) ?
            0 :
            Number(infor?.additionalServiceCost?.underTwentyYearsOldFee);

        return compensationFeeNumber + underTwentyYearsOldFeeNumber;
    }, [infor?.additionalServiceCost?.compensationFee, infor?.additionalServiceCost?.underTwentyYearsOldFee]);

    const additionalServices: OptionBookingTable[] = useMemo(() => {
        let services: OptionBookingTable[] = [];
        const { additionalServiceCost: { optionSelect = [], compensation = [], dropOff = [], underTwentyYearsOld = [], extraMileage = [] } } = infor || { additionalServiceCost: {} };

        services = [
            ...optionSelect.map(item => ({ ...item, format: OPTION_FORMAT.Option })),
            ...compensation.map(item => ({ ...item, format: OPTION_FORMAT.Insurance })),
            ...underTwentyYearsOld.map(item => ({ ...item, format: OPTION_FORMAT.Option, isUnder20: true })),
            ...extraMileage.map(item => ({ ...item, format: OPTION_FORMAT.ExtraMileage })),
            ...dropOff.map(item => ({ ...item, format: OPTION_FORMAT.DropOff }))
        ];
        return services;
    }, [infor]);

    // columns
    const columns: ColumnDef<OptionBookingTable>[] = [
        {
            accessorKey: 'name',
            header: t('field.option_page.option_name'),
            size: 400,
            meta: {
                headerClassName: 'center-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => {
                return (
                    <div className='text-center break-words'>
                        {
                            original.id === 0 ?
                                (
                                    <div className='text-customRed font-medium'>
                                        {t('price_list.under_21_check')}
                                    </div>
                                ) :
                                <div className='font-medium break-words'>{original.name}</div>
                        }
                    </div>
                );
            },
            enableSorting: false
        },
        // {
        //     accessorKey: 'price',
        //     header: `${t('field.unit_price')}`,
        //     size: 200,
        //     enableSorting: false,
        //     cell: ({ row: { original } }) => {
        //         return (
        //             <div>
        //                 {original?.price && t('currency.currency_symbol')}
        //                 {formatToCurrency(original?.price?.toString())}
        //             </div>
        //         );
        //     }
        // },
        {
            accessorKey: 'quantity',
            header: t('field.quantity'),
            enableSorting: false,
            size: 50,
            meta: {
                headerClassName: 'center-sub-header-item',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='text-center'>{original.value ?? ''}</div>
            )
        },
        {
            accessorKey: 'x',
            header: '',
            size: 10,
            meta: {
                headerClassName: 'center-sub-header-item p-0',
                cellClassName: 'p-0'
            },
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex justify-center items-center'>{original.value && original.numberOfWeeks ? <XCusIcon className='size-[10px]' /> : ''}</div>
            )
        },
        {
            accessorKey: 'numberOfWeeks',
            header: t('field.number_of_weeks'),
            size: 50,
            meta: {
                headerClassName: 'center-sub-header-item',
                cellClassName: ''
            },
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='text-center'>{original.numberOfWeeks}</div>
            )
        },
        {
            accessorKey: 'fee',
            header: t('field.fee'),
            size: 150,
            meta: {
                headerClassName: 'center-sub-header-item',
                cellClassName: ''
            },
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <p className='text-center whitespace-nowrap'>
                    {formatToCurrency(original?.totalPrice?.toString())}
                    {original?.totalPrice && t('currency.currency_unit')}
                </p>
            )
        }
    ];

    return (
        <div className='relative'>
            <StepWrapper currentStep={3}>
                <div className='flex flex-col md:gap-20 xsm:gap-[60px] gap-[30px] md:mt-20 xsm:mt-[60px] mt-[30px]'>
                    {/* Bike infor */}
                    <div>
                        {
                            (infor?.additionalServiceCost?.underTwentyYearsOld?.length > 0 && infor?.additionalServiceCost?.underTwentyYearsOld?.[0]?.id === 0) && (
                                <div className='xsm:text-[1rem] text-[14px] text-red-3 font-semibold xsm:mb-4 mb-2'>
                                    {t('booking_bike.sentences.under_20_ages_money')}
                                </div>
                            )
                        }

                        {/* Show time date */}
                        <div className={cn(
                            'flex flex-col xsm:gap-2.5 gap-5 md:mt-20 xsm:mt-[60px] mt-[30px]',
                            (infor?.additionalServiceCost?.underTwentyYearsOld?.length > 0 && infor?.additionalServiceCost?.underTwentyYearsOld?.[0]?.id === 0) && '!mt-2'
                        )}
                        >
                            <div className='flex items-center gap-1'>
                                <div className='size-3 bg-customOrange'></div>
                                <p className='font-semibold'>レンタル予定日時</p>
                            </div>
                            <div className='bg-subtle xsm:p-10 px-5 py-[30px] flex flex-col gap-2.5 rounded-[5px]'>
                                <div className='flex flex-col border-b border-border-gray gap-2.5 pb-2.5'>
                                    <div className='flex xsm:justify-start justify-between font-medium gap-5'>
                                        <div className='xsm:w-[200px] point-375:w-[140px] w-[120px]'>{t('booking_bike.field.rental_date_time')}</div>
                                        <div className='flex-1 text-left'>{dataSession?.startTime ? moment.parseZone(dataSession?.startTime).format(DISPPLAY_MOMENT_YMD_HM_FULLWIDTH) : '--'}</div>
                                    </div>
                                    <div className='flex xsm:justify-start justify-between font-medium gap-5'>
                                        <div className='xsm:w-[200px] point-375:w-[140px] w-[120px]'>{t('booking_bike.field.return_date_time')}</div>
                                        <div className='flex-1 text-left'>{dataSession?.endTime ? moment.parseZone(dataSession?.endTime).format(DISPPLAY_MOMENT_YMD_HM_FULLWIDTH) : '--'}</div>
                                    </div>
                                    <div className='flex xsm:justify-start justify-between font-bold gap-5'>
                                        <div className='xsm:w-[200px] point-375:w-[140px] w-[120px]'>{t('booking_bike.field.total_rental_time')}</div>
                                        <div className='flex-1 text-left text-customRed'>{totalTime}</div>
                                    </div>
                                </div>
                                <div className='flex xsm:justify-start justify-between font-bold gap-5'>
                                    <div className='xsm:w-[200px] point-375:w-[140px] w-[120px]'>{t('booking_bike.field.total_amount')}</div>
                                    <div className='flex-1 text-left text-customRed'>
                                        {formatCurrency(totalTimeCost)}
                                        {t('currency.currency_unit')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Option */}
                    {
                        additionalServices.length > 0 && (
                            <div className='flex flex-col xsm:gap-2.5 gap-5'>
                                <div className='flex items-center gap-1'>
                                    <div className='size-3 bg-customOrange'></div>
                                    <p className='font-semibold'>追加されたオプション</p>
                                </div>
                                <div className=''>
                                    <Table
                                        headerClassName='bg-subtle text-text font-medium xsm:py-[15px] sm:px-[30px] p-2.5'
                                        cellClassName='xsm:text-fs-mid14 text-[12px] xsm:py-[15px] sm:px-[30px] p-2.5'
                                        data={additionalServices}
                                        columns={columns}
                                        showTableFooter={false}
                                        showPagination={false}
                                        showPagingInfo={false}
                                        showSelectPageSize={false}
                                    />
                                </div>

                            </div>
                        )
                    }

                    {/* Coupon */}
                    <div className=''>
                        <div className='flex items-center gap-1'>
                            <div className='size-3 bg-customOrange'></div>
                            <p className='font-semibold'>クーポン追加</p>
                        </div>
                        <RadioGroup
                            onValueChange={(value) => {
                                setCoupon(value);
                                const dataSession = getFromSession(uuid || '');

                                onFetch({
                                    ...dataSession,
                                    couponSelectedCode: value
                                }, false);
                            }}
                            value={coupon}
                            className='flex flex-col gap-4'
                        >
                            {
                                ((couponForever && Object.keys(couponForever).length > 0) || (couponList && couponList?.length > 0)) && (
                                    <>
                                        <div className=''>
                                            {
                                                couponForever && Object.keys(couponForever).length > 0 && (
                                                    <div className='flex flex-col border-[#F2F2F2] border-b gap-2 pb-4 mt-2'>
                                                        <p>{t('booking_bike.field.coupon_forever')}</p>
                                                        <div className='grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 md:gap-6 xsm:gap-4 gap-2'>
                                                            <div className='flex border border-[#DE0001] rounded-[0.25rem] gap-4 items-center px-3 py-[6px]'>
                                                                <RadioGroupItem
                                                                    value={couponForever.couponCode}
                                                                    id='radio-123'
                                                                />
                                                                <CouponItem
                                                                    coupon={couponForever}
                                                                />
                                                            </div>
                                                        </div>

                                                    </div>
                                                )
                                            }

                                        </div>
                                        {
                                            (couponList && couponList?.length > 0 && couponList.filter(c => c.couponType !== COUPON_TYPE.FOREVER_USE).length > 0 && (
                                                <div className='grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 md:gap-6 xsm:gap-4 gap-2'>
                                                    {couponList.filter(c => c.couponType !== COUPON_TYPE.FOREVER_USE).map((item, index) => (
                                                        <div key={item.id} className='flex border border-[#DE0001] rounded-[0.25rem] gap-4 items-center px-3 py-[6px]'>
                                                            <RadioGroupItem
                                                                value={item.couponCode}
                                                                id={`radio-${item?.id}-${index}`}
                                                            />
                                                            <CouponItem coupon={item} />
                                                        </div>
                                                    ))}
                                                </div>
                                            ))
                                        }
                                    </>

                                )
                            }

                        </RadioGroup>
                        <div className='flex flex-col gap-2.5 mt-4'>
                            <div className='flex point-375:flex-row flex-col point-375:gap-5 gap-2.5 lg:w-2/5 md:w-2/3'>
                                <Input
                                    type='text'
                                    placeholder={t('placeholder.enter_coupon')}
                                    value={searchCoupon}
                                    className='h-10 bg-white placeholder:font-medium border-border-gray'
                                    onChange={(e) => {
                                        setSearchCoupon(e.target.value);
                                    }}
                                />
                                <ButtonCus
                                    type='submit'
                                    variant='default'
                                    size='default'
                                    className='min-w-[70px] h-10 point-375:w-fit w-full !rounded-[5px] border border-border-gray hover:border-customRed'
                                    disabled={!searchCoupon || (searchCoupon.trim() === '')}
                                    onClick={onAddCoupon}
                                >
                                    {t('button.apply')}
                                </ButtonCus>
                            </div>
                            <p className='text-black-6 xsm:text-[0.8125rem] text-[12px]'>
                                {t('booking_bike.sentences.note_coupon')}
                            </p>
                        </div>
                    </div>

                    <div className='h-[1px] w-full bg-border-gray'></div>

                    {/* Calculate Amount */}
                    <div className='flex justify-center'>
                        <div className='w-[700px] max-w-full flex flex-col gap-2.5'>
                            <div className='bg-subtle rounded-[5px] xsm:p-10 py-[30px] px-5 leading-[30px] flex flex-col gap-2.5'>
                                <div className='flex flex-col border-b font-medium border-border-gray gap-2.5 pb-2.5'>
                                    <div className='flex justify-between gap-6'>
                                        <div>{t('booking_bike.field.rental_amount')}</div>
                                        <div className=''>
                                            {formatCurrency(infor?.totalCost?.totalBooking || 0)}
                                            {t('currency.currency_unit')}
                                        </div>
                                    </div>

                                    <div className='flex justify-between gap-6'>
                                        <div>{t('booking_bike.field.compensation')}</div>
                                        <div className=''>
                                            {formatCurrency(compensationFee || 0)}
                                            {t('currency.currency_unit')}
                                        </div>
                                    </div>
                                    {
                                        infor?.totalCost?.coupon > 0 && (
                                            <div className='flex justify-between gap-6'>
                                                <div>{t('booking_bike.field.coupon')}</div>
                                                <div className=''>
                                                    &#9650;
                                                    {formatCurrency(infor?.totalCost?.coupon || 0)}
                                                    {t('currency.currency_unit')}
                                                </div>
                                            </div>
                                        )
                                    }
                                </div>

                                <div className='flex justify-between font-bold gap-3'>
                                    <div className=''>
                                        {t('booking_bike.field.total_payment')}
                                    </div>
                                    <div className='text-fs-mid24 text-customRed'>
                                        {formatCurrency(infor?.totalCost?.totalPayment || 0)}
                                        {t('currency.currency_unit')}
                                    </div>
                                </div>
                            </div>

                            <Form {...form}>
                                <form>
                                    <FormField
                                        control={form.control}
                                        name='agree'
                                        render={({ field }) => (
                                            <FormItem className='flex items-center leaidng-[30px]'>
                                                <FormControl>
                                                    <Checkbox
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                        className='bg-[#fff] border-text'
                                                    />
                                                </FormControl>
                                                <FormLabel className='text-text cursor-pointer font-normal mb-0 ml-2.5'>
                                                    <Trans
                                                        i18nKey='booking_bike.sentences.agree_payment'
                                                        values={{ term_condition: t('booking_bike.sentences.term_condition') }}
                                                        components={{
                                                            1: (
                                                                <a
                                                                    href={`${URL_CONFIG.DOMAIN_WORDPRESS.url + WORDPRESS_PATH.terms[isEk ? 'hrefEk' : 'href']}`}
                                                                    className='text-customRed underline'
                                                                    target='_blank'
                                                                    rel='noreferrer'
                                                                />
                                                            )
                                                        }}
                                                    />
                                                </FormLabel>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </form>
                            </Form>

                        </div>
                    </div>

                    <div className='flex sm:flex-row flex-col justify-center lg:gap-[80px] md:gap-[60px] gap-[30px] items-center'>
                        <AnimateButton
                            type='button'
                            side='left'
                            parentClassName='md:w-[420px] xsm::w-80 w-full sm:order-1 order-2'
                            className=' min-h-[67px]'
                            contentComp={t('button.back')}
                            outline
                            onClick={onBack}
                        >
                        </AnimateButton>
                        <AnimateButton
                            type='button'
                            onClick={onPayment}
                            parentClassName='md:w-[420px] xsm::w-80 w-full sm:order-2 order-1'
                            className=' min-h-[67px]'
                            contentComp={t('button.payment')}
                            disabled={!watchAgree}
                        >
                        </AnimateButton>
                    </div>
                </div>
            </StepWrapper>

        </div>
    );
});
